import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const ShopifyCustomersPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Sample data - replace with actual Shopify API call
  const sampleCustomers = [
    {
      id: 1,
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      phone: '+32 123 456 789',
      location: 'Brussels, Belgium',
      ordersCount: 15,
      totalSpent: 1250.50,
      status: 'active',
      avatar: null,
      tags: ['VIP', 'Wholesale'],
      createdAt: '2024-01-15',
    },
    {
      id: 2,
      firstName: 'Jane',
      lastName: 'Smith',
      email: 'jane.smith@example.com',
      phone: '+32 987 654 321',
      location: 'Antwerp, Belgium',
      ordersCount: 8,
      totalSpent: 650.00,
      status: 'active',
      avatar: null,
      tags: ['Regular'],
      createdAt: '2024-02-20',
    },
  ];

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual Shopify API call
      // const response = await getShopifyCustomers();
      setTimeout(() => {
        setCustomers(sampleCustomers);
        setLoading(false);
      }, 1000);
    } catch (error) {
      toast.error('Failed to fetch customers');
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, customer) => {
    setMenuAnchor(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedCustomer(null);
  };

  const handleViewOrders = () => {
    toast.info(`Viewing orders for ${selectedCustomer?.firstName} ${selectedCustomer?.lastName}`);
    handleMenuClose();
  };

  const handleSendEmail = () => {
    toast.info(`Sending email to ${selectedCustomer?.email}`);
    handleMenuClose();
  };

  const filteredCustomers = customers.filter((customer) =>
    searchTerm === '' ||
    `${customer.firstName} ${customer.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  const formatCurrency = (amount) => {
    return `€${amount.toFixed(2)}`;
  };

  if (loading && customers.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5 }}>
              {t('online_store_customers') || 'Online Store Customers'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('manage_shopify_customers') || 'Manage your Shopify customers and their orders'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('refresh') || 'Refresh'}>
              <IconButton onClick={fetchCustomers} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              sx={{ borderRadius: 2 }}
            >
              {t('export') || 'Export'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                },
              }}
            >
              {t('add_customer') || 'Add Customer'}
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      {t('total_customers') || 'Total Customers'}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea', mt: 1 }}>
                      {customers.length}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#e0e7ff', color: '#667eea', width: 56, height: 56 }}>
                    <PersonIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      {t('total_orders') || 'Total Orders'}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981', mt: 1 }}>
                      {customers.reduce((sum, c) => sum + c.ordersCount, 0)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#d1fae5', color: '#10b981', width: 56, height: 56 }}>
                    <ShoppingCartIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      {t('total_revenue') || 'Total Revenue'}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6', mt: 1 }}>
                      {formatCurrency(customers.reduce((sum, c) => sum + c.totalSpent, 0))}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#ede9fe', color: '#8b5cf6', width: 56, height: 56 }}>
                    <ShoppingCartIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      {t('active_customers') || 'Active Customers'}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b', mt: 1 }}>
                      {customers.filter(c => c.status === 'active').length}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#fef3c7', color: '#f59e0b', width: 56, height: 56 }}>
                    <PersonIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder={t('search_customers') || 'Search customers by name or email...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              sx={{ borderRadius: 2, minWidth: 120 }}
            >
              {t('filters') || 'Filters'}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Customers Table */}
      <Paper sx={{ borderRadius: 2, boxShadow: 2 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f9fafb' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>{t('customer') || 'Customer'}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>{t('contact') || 'Contact'}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>{t('location') || 'Location'}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#374151' }}>{t('orders') || 'Orders'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#374151' }}>{t('total_spent') || 'Total Spent'}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#374151' }}>{t('status') || 'Status'}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#374151' }}>{t('actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <PersonIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {t('no_customers_found') || 'No customers found'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('try_different_search') || 'Try adjusting your search or filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((customer) => (
                    <TableRow
                      key={customer.id}
                      hover
                      sx={{ '&:hover': { bgcolor: '#f9fafb', cursor: 'pointer' } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#667eea' }}>
                            {customer.firstName[0]}{customer.lastName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {customer.firstName} {customer.lastName}
                            </Typography>
                            {customer.tags.map((tag, index) => (
                              <Chip
                                key={index}
                                label={tag}
                                size="small"
                                sx={{ mr: 0.5, mt: 0.5, height: 20, fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                            <Typography variant="body2">{customer.email}</Typography>
                          </Box>
                          {customer.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                              <Typography variant="body2" color="text.secondary">{customer.phone}</Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                          <Typography variant="body2">{customer.location}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Badge badgeContent={customer.ordersCount} color="primary" max={99}>
                          <ShoppingCartIcon color="action" />
                        </Badge>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#10b981' }}>
                          {formatCurrency(customer.totalSpent)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={customer.status}
                          color={getStatusColor(customer.status)}
                          size="small"
                          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, customer)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredCustomers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewOrders}>
          <ShoppingCartIcon sx={{ mr: 1, fontSize: 20 }} />
          {t('view_orders') || 'View Orders'}
        </MenuItem>
        <MenuItem onClick={handleSendEmail}>
          <EmailIcon sx={{ mr: 1, fontSize: 20 }} />
          {t('send_email') || 'Send Email'}
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default ShopifyCustomersPage;
